import { apiDRSSubcontractorTransportDeliveryRemove } from '@src/apis/drs/drs-subcontractor-transport-delivery-remove'
import { apiDRSSubcontractorTransportDeliveryUpdate } from '@src/apis/drs/drs-subcontractor-transport-delivery-update'
import { useAppStore } from '@src/AppStore'
import CompCreateButton from '@src/components/Buttons/CreateButton'
import { PAETableMoreActionType } from '@src/components/Form/comps/ActionMenu'
import FormPAETable from '@src/components/Form/PAETable'
import Modal from '@src/components/FormModal/Modal'
import useModal from '@src/components/FormModal/useModal'
import FormPanel from '@src/components/FormPanel/FormPanel'
import { useConfirm } from '@src/components/GlobalConfirmDialog'
import { useSnackbar } from '@src/components/GlobalSnackbar'
import MarginTop from '@src/components/Layout/MarginTop'
import { DRSPageType, mapDRSPageTypeToRoleId } from '@src/modules/DRS/DRSPage'
import { useDRS } from '@src/modules/DRS/useDRS'
import { doubleUp } from '@src/modules/DRS/utils/doubleUp'
import { mapMoreActions } from '@src/modules/DRS/utils/mapMoreActions'
import { PAE } from '@src/types/drs/PAE'
import { SubcontractorTransportDeliveryPAE } from '@src/types/drs/subcontractor/SubcontractorTransportDelivery'
import { PackFormType } from '@src/types/PackFormType'
import { ProjectTypeId, normalizeProjectTypeId } from '@src/types/ProjectTypeId'
import { TransportTypeId } from '@src/types/TransportTypeId'
import React, { useState } from 'react'
import { useParams } from 'react-router'
import styled from 'styled-components'
import { getSupplierAgreementActivityRowData } from '../utils/getSupplierAgreementActivityRowData'
import { transformFromFormData } from '../utils/transformFromFormData'
import DRSSubcontractorTransportDeliverysFields, { DRSSubcontractorTransportDeliveryRow } from './TransportDeliveryFields'

const NULL_VALUES = {
  comments: null,
  docketNumber: null,
  endTime: null,
  quantity: null,
  startTime: null,
  supplier: null,
  supplierAgreement: null,
  weight: null,
}
const ZERO_VALUES = {
  comments: null,
  docketNumber: null,
  endTime: null,
  quantity: '0',
  startTime: null,
  supplier: null,
  supplierAgreement: null,
  weight: '0',
}

interface Props {
  projectTypeId: ProjectTypeId.Asphalt | ProjectTypeId.SpraySeal,
}

const CompDRSSubcontractorTransportDelivery = (props: Props) => {
  const {
    projectTypeId,
  } = props
  const normalizedType = normalizeProjectTypeId(projectTypeId)
  const { id } = useParams<{ id: string }>()
  const { drs, drsId, updateViaPath, updateSectionViaPath, drsPageType, fetchDRS, refresh } = useDRS()
  const isPlan = drsPageType === DRSPageType.Plan
  const isEdit = drsPageType === DRSPageType.Edit
  const updatingColumn: PAE.Plan | PAE.Edit = isPlan ? PAE.Plan : PAE.Edit
  const { alertError, alertDone, } = useSnackbar()
  const { confirm } = useConfirm()
  const [open, modalData, openAndSetData, closeModal] = useModal<DRSSubcontractorTransportDeliveryRow>({})
  const { depotId, stateId, serialId } = useAppStore()
  
  // Add a state to force re-render when data changes
  const [dataVersion, setDataVersion] = useState(0)
  
  // Force increment when refresh is called to ensure component re-renders
  const forceRefresh = async () => {
    await refresh()
    setDataVersion(prev => prev + 1)
  }
  if (drs === null || drs.subcontractor[normalizedType] === null) {
    return null
  }
  const handleSave = async (form: DRSSubcontractorTransportDeliveryRow) => {
    const finalFormData = transformFromFormData(form, drs.jobDetails.date)
    if (modalData === null) {
      const retained = getRetained()
      update([
        ...retained,
        {
          workType: {
            id: projectTypeId === ProjectTypeId.Asphalt ? 'ASPHALT' : 'SPRAYSEAL',
          },
          [updatingColumn]: {
            ...finalFormData,
            transportType: {
              id: TransportTypeId.Delivery,
            }
          }
        }
      ])
    } else {
      updateItemByValues(form, finalFormData)
    }
  }
  const handleRevert = async (form: DRSSubcontractorTransportDeliveryRow) => {
    updateItemByValues(form, NULL_VALUES)
  }
  const handleMarkZero = async (form: DRSSubcontractorTransportDeliveryRow) => {
    updateItemByValues(form, ZERO_VALUES)
  }
  const handleDelete = async (form: any[]) => {
    var recordsAPI = [];
    var formData =typeof form == 'object' && form.length > 0 ?  form : [form];
    const retained = getRetained();
    if (formData.length > 0) {
      if (retained.length == formData.length) {
        recordsAPI = retained;
      } else {
        for (var i = 0; i < formData.length; i++) {
          recordsAPI.push(retained[formData[i]['_no_']]);
        }
      }
    }
  
    if (recordsAPI.length > 0)
      deleteRecords(recordsAPI);
  }
  const deleteRecords = async (data: any[]) => {
    try {
      await apiDRSSubcontractorTransportDeliveryRemove({
        drsId: drsId,
        normalizedType,
        assets: data,
        reqSid: serialId,
        reqRole: mapDRSPageTypeToRoleId(drsPageType),
      })
      await forceRefresh()
      closeModal()
      alertDone()
    } catch (e) {
      console.error('Error deleting transport delivery:', e)
      alertError()
    }
  }
  const handleDuplicate = async (form: DRSSubcontractorTransportDeliveryRow) => {
    const retained = getRetained()
    update([
      ...retained,
      {
        workType: {
          id: projectTypeId === ProjectTypeId.Asphalt ? 'ASPHALT' : 'SPRAYSEAL',
        },
        [updatingColumn]: {
          ...NULL_VALUES,
          supplier: form.supplier,
          supplierAgreement: form.supplierAgreement,
          transportType: {
            id: TransportTypeId.Delivery,
          }
        }
      }
    ])
  }
  const getRetained = () => {
    const retainedAssets = drs.subcontractor[normalizedType].deliveryTransport
    return retainedAssets
  }
  const updateItemByValues = (form: DRSSubcontractorTransportDeliveryRow, values: any) => {
    const retained = getRetained()
    if (form._no_ === undefined || form._type_ === undefined) {
      return
    }
    const updatingIndex = form._no_
    const data = [
      ...retained.slice(0, updatingIndex),
      {
        ...retained[updatingIndex],
        [updatingColumn]: {
          ...values,
          transportType: {
            id: TransportTypeId.Delivery,
          }
        }
      },
      ...retained.slice(updatingIndex + 1),
    ]
    update(data)
  }
  const update = async (data: any[]) => {
    try {
      await apiDRSSubcontractorTransportDeliveryUpdate({
        drsId: drsId,
        normalizedType,
        assets: data,
        reqSid: serialId,
        reqRole: mapDRSPageTypeToRoleId(drsPageType),
      })
      await forceRefresh()
      closeModal()
      alertDone()
    } catch (e) {
      console.error('Error updating transport delivery:', e)
      alertError()
    }
  }
  const getTableData = () => {
    const assets: SubcontractorTransportDeliveryPAE[] = drs.subcontractor[normalizedType].deliveryTransport
    const packFormType = PackFormType.DRS_SUBCONTRACTOR_TRANSPORT
    // const tripled = tripleUp<SubcontractorTransportDeliveryPAE>(assets, drsPageType, packFormType)
    const tripled = doubleUp<SubcontractorTransportDeliveryPAE>(assets, drsPageType)
    const tripledWithActions = tripled.map((data: any) => mapMoreActions(data, drsPageType, undefined, [PAETableMoreActionType.Delete, PAETableMoreActionType.Duplicate]))
    return tripledWithActions
  }
  let finalFields = DRSSubcontractorTransportDeliverysFields
  return <Wrapper>
    <FormPanel title={`Subcontractor Transport Delivery`}>
      <FormPAETable<DRSSubcontractorTransportDeliveryRow>
        key={`transport-delivery-${dataVersion}-${drs?.subcontractor[normalizedType]?.deliveryTransport?.length || 0}`}
        fields={finalFields}
        data={getTableData()}
        isSpecialDelete={true}
        showNo={true}
        editable={true}
        isSelectable={true}
        onEditRow={(row: DRSSubcontractorTransportDeliveryRow) => {
          openAndSetData(getSupplierAgreementActivityRowData(row))
        }}
        showMoreActions={isPlan || isEdit}
        onRevert={handleRevert}
        onMarkZero={handleMarkZero}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
      />
      {(isPlan || isEdit) &&
        <MarginTop>
          <CompCreateButton onClick={() => {
            openAndSetData(null)
          }}>
            Add New Transport
          </CompCreateButton>
        </MarginTop>
      }
    </FormPanel>

    {open &&
      <Modal<DRSSubcontractorTransportDeliveryRow>
        maxWidth={'sm'}
        open={open}
        title='Subcontractor Transport Delivery'
        data={{
          rowData: modalData,
          fields: finalFields,
          columns: [
            {
              title: 'Transport Information',
              fieldKeys: [
                'supplier',
                'supplierAgreement',
                'activity',
                'comments',
              ],
            },
            {
              title: '',
              fieldKeys: [
                'weight',
                'startTime',
                'endTime',
                'quantity',
                'docketNumber',
              ],
            },
          ],
          extra: {
            depotId,
            stateId,
          }
        }}
        onSuccess={handleSave}
        onClose={closeModal}
        onCancel={closeModal}
      />
    }
  </Wrapper>
}

const Wrapper = styled.div`

`

export default CompDRSSubcontractorTransportDelivery